import { StrictMode, useEffect, useState } from 'react'
import { ClerkProvider } from '@clerk/clerk-react'
import { createRoot } from 'react-dom/client'
import './index.css'
import App from './App.tsx'

// Create a ThemeProvider component
function ThemeProvider({ children }: { children: React.ReactNode }) {
  return (
    <div className="light">
      {children}
    </div>
  )
}

type ClerkRuntimeConfig = {
  clerk_publishable_key: string | null
}

function BootstrapApp() {
  const fallbackPublishableKey = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY
  const [publishableKey, setPublishableKey] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false

    const loadPublishableKey = async () => {
      try {
        const response = await fetch('/runtime-config', {
          headers: { Accept: 'application/json' },
          credentials: 'same-origin',
        })

        if (!response.ok) {
          if (!fallbackPublishableKey) {
            throw new Error(
              `Failed to load Clerk config from runtime endpoint (${response.status} ${response.statusText})`
            )
          }
        } else {
          const data = (await response.json()) as ClerkRuntimeConfig
          if (data.clerk_publishable_key) {
            if (!cancelled) {
              setPublishableKey(data.clerk_publishable_key)
            }
            return
          }

          if (!fallbackPublishableKey) {
            throw new Error('Clerk publishable key not configured in worker')
          }
        }
      } catch (err) {
        if (!fallbackPublishableKey) {
          if (!cancelled) {
            setError(
              err instanceof Error
                ? err.message
                : 'Failed to load Clerk publishable key'
            )
          }
          return
        }
      }

      if (!cancelled) {
        setPublishableKey(fallbackPublishableKey)
      }
    }

    if (fallbackPublishableKey) {
      setPublishableKey(fallbackPublishableKey)
    } else {
      setPublishableKey(null)
      void loadPublishableKey()
    }

    return () => {
      cancelled = true
    }
  }, [fallbackPublishableKey])

  if (!publishableKey) {
    if (error) {
      return <div className="h-screen w-screen flex items-center justify-center text-center">{error}</div>
    }

    return (
      <div className="h-screen w-screen flex items-center justify-center">
        <div className="text-lg">Loading auth configuration...</div>
      </div>
    )
  }

  return (
    <ClerkProvider publishableKey={publishableKey}>
      <ThemeProvider>
        <App />
      </ThemeProvider>
    </ClerkProvider>
  )
}

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <BootstrapApp />
  </StrictMode>,
)
